import React, { useEffect } from 'react'
import Header, { HeaderVariant } from '../Elements/Header'
import CardFormHeader from '../Card/CardFormHeader'
import { useTranslation } from 'react-i18next'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { FormDivider } from '../Elements/FormDivider'
import { Box, TextField } from '@mui/material'
import CheckboxControl from '../Checkboxes/CheckboxControl'
import { useTranscriptContext } from '../Context/TranscriptContext'
import { useAccountContext } from '../Context/AccountContext'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import { useTheme } from '@mui/material/styles'
import { Transcript } from '../../swagger'
import studentKeyToStudentIdForTranscript from '../../utils/studentKeyToStudentIdForTranscript'
import DateField, {
  DateFieldVariant,
  dateFormatMMDDYYY,
} from '../Elements/DateField'
import { dateToDashStringReinterpretedAsLocal } from '../../utils/dateUtility'
import { useUser } from '../../UserContext'
import { parse } from 'date-fns'

export interface StudentInformationHeaderProps {
  isFieldDisabled?: boolean
  isSameAddress?: boolean
  handleSameAddress: (value: boolean) => void
}

export const StudentInformationHeader: React.FC<
  StudentInformationHeaderProps
> = ({ isFieldDisabled, isSameAddress, handleSameAddress }) => {
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()
  const {
    updateTranscriptDetails,
    transcriptDetails,
    transcriptValidity,
    updateFormValidity,
  } = useTranscriptContext()
  const { students } = useAccountContext()
  const { user } = useUser()
  const theme = useTheme()

  const textFieldOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    const emailInputName = 'studentEmail'
    const isEmailField = name === emailInputName

    // Database limits student email field to 320 characters
    if (name === emailInputName && value.length > 320) {
      return
    }

    updateFormValidity({
      ...transcriptValidity,
      [name]: {
        input: true,
        ...(isEmailField && { email: true }),
      },
    })

    updateTranscriptDetails({
      ...transcriptDetails,
      [name]: value,
    })
  }

  const dateFieldOnChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value?: string
  ) => {
    /*
     * Datefield should be disabled if there is
     * no studentKey, this should never be true. Thank you typescript XD.
     */
    if (!transcriptDetails.student?.studentKey) return

    updateTranscriptDetails({
      ...transcriptDetails,
      studentDateOfBirth: value
        ? parse(value, dateFormatMMDDYYY, new Date())
        : undefined,
    })

    updateFormValidity({
      ...transcriptValidity,
      dateOfBirth: {
        input: true,
      },
    })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault()
  }

  useEffect(() => {
    /**
     * This effect only runs when creating a new transcript.
     * If `lastName` is still undefined and a `user` is available,
     * we default `lastName` to the user's last name.
     *
     * For existing transcripts, the backend now makes sure that
     * if `lastName` is missing in the database, it will return the `user.lastName`
     * associated as the parent of the transcript.
     */
    if (user && transcriptDetails.lastName === undefined) {
      updateTranscriptDetails({
        ...transcriptDetails,
        lastName: user.lastName,
      })
    }
  }, [updateTranscriptDetails, transcriptDetails, user])

  return (
    <section aria-labelledby="student-information-header">
      <CardFormHeader
        header={
          <Header
            id="student-information-header"
            component={'h2'}
            variant={HeaderVariant.Card}
            headerName={t(
              'Transcripts.StudentInformationHeader.Header.StudentInformation',
              'Student Information'
            )}
          />
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.25),
        }}
      >
        <Box
          marginLeft={theme.spacing(2)}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              sm: '1fr',
              md: '1fr 1fr',
            },
          }}
          gap={theme.spacing(2.5)}
        >
          <DropDown
            formControlProps={{
              marginBottom: showOnDesktop ? '' : theme.spacing(0),
              transform: 'translateY(-2px)',
            }}
            id="studentName"
            menuOptions={students.map((student) => {
              return { name: student.firstName, id: student.studentKey }
            })}
            required
            helperText={
              !transcriptValidity.studentKey.input
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentKey',
                    'Student is required.'
                  )
                : undefined
            }
            error={!transcriptValidity.studentKey.input}
            handleSelection={(
              selection: string,
              _: string,
              selectionId: string | number
            ) => {
              updateFormValidity({
                ...transcriptValidity,
                studentKey: { input: true },
              })
              updateTranscriptDetails({
                ...transcriptDetails,
                /**
                 * We update both a CreateTranscript and Transcript
                 * student selection.
                 */
                studentKey: +selectionId,
                student: {
                  firstName: selection,
                  studentKey: +selectionId,
                },
              })
            }}
            disabled={isFieldDisabled}
            label={t(
              'Transcripts.StudentInformationHeader.Field.StudentName',
              'Student Name'
            )}
            variant={DropDownVariant.FormField}
            fullWidth
            /**
             * It will always be as a `Transcript` even though we're not
             * providing the name to the createTranscript call. We can utilize
             * the display name & key on selection.
             */
            value={(transcriptDetails as Transcript).student?.firstName ?? ''}
            cssProps={{
              padding: 0,
              height: 'auto',
              width: '100%',
              textAlign: 'left',
            }}
          />
          <TextField
            id="lastName"
            name="lastName"
            label={t(
              'Transcripts.StudentInformationHeader.Field.lastName',
              'Student Last Name'
            )}
            variant="filled"
            sx={{
              width: '100%',
            }}
            value={transcriptDetails?.lastName ?? ''}
            disabled={isFieldDisabled}
            fullWidth
            onChange={textFieldOnChange}
            error={!transcriptValidity.lastName.input}
            helperText={
              !transcriptValidity.lastName.input
                ? t(
                    'Transcripts.StudentInformationHeader.Error.LastName',
                    'Student Last Name Is Required.'
                  )
                : ''
            }
          />

          <TextField
            id="studentID"
            name="studentID"
            label={t(
              'Transcripts.StudentInformationHeader.Field.StudentID',
              'Student ID'
            )}
            variant="filled"
            sx={{
              width: '100%',
            }}
            value={studentKeyToStudentIdForTranscript(
              transcriptDetails.student?.studentKey ??
                transcriptDetails.studentKey
            )}
            fullWidth
            disabled
          />

          <DateField
            id="dateOfBirthEntry"
            inputProps={{
              'aria-label': t(
                'Transcripts.StudentInformationHeader.Field.DateOfBirth',
                'Date of Birth Entry'
              ),
            }}
            disabled={isFieldDisabled || !transcriptDetails.student?.studentKey}
            variant="filled"
            type={isFieldDisabled ? 'text' : 'date'}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={
              !!transcriptDetails.studentDateOfBirth
                ? dateToDashStringReinterpretedAsLocal(
                    transcriptDetails.studentDateOfBirth
                  )
                : ''
            }
            name="dateOfBirthEntry"
            onChangeForParent={dateFieldOnChange}
            helperTextForParent={t(
              'Transcripts.StudentInformationHeader.Error.DateOfBirthRequired',
              'Student Date of Birth Is Required.'
            )}
            error={!transcriptValidity.dateOfBirth.input}
            dateVariant={DateFieldVariant.DateOfBirth}
            onKeyDown={onKeyDown}
          />
          <TextField
            id="studentEmail"
            name="studentEmail"
            label={t(
              'Transcripts.StudentInformationHeader.Field.Email',
              'Email'
            )}
            variant="filled"
            disabled={isFieldDisabled}
            sx={{
              width: '100%',
            }}
            value={transcriptDetails.studentEmail ?? ''}
            onChange={textFieldOnChange}
            helperText={
              !transcriptValidity.studentEmail.email
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentEmailNotValid',
                    'Student Email must be a valid email.'
                  )
                : !transcriptValidity.studentEmail.input
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentEmailRequired',
                    'Student Email Is Required.'
                  )
                : null
            }
            FormHelperTextProps={{
              disabled: false,
            }}
            fullWidth
            error={
              !transcriptValidity.studentEmail.input ||
              !transcriptValidity.studentEmail.email
            }
          />
        </Box>
      </Box>
      <FormDivider />
      <CheckboxControl
        disabled={isFieldDisabled}
        label={t(
          'Transcripts.StudentInformationHeader.Checkbox.SameAs',
          'Student Address same as School Address'
        )}
        checkboxValue={!!isSameAddress}
        OnChange={(checked) => handleSameAddress(checked)}
        name={'same-address-as-school'}
      />
    </section>
  )
}

export default StudentInformationHeader
